/**
 * Utility functions for checking measurement values against target ranges
 */

/**
 * Get alert status for a measurement value
 * @param {number} value - The measurement value
 * @param {number} min - Minimum target value
 * @param {number} max - Maximum target value
 * @returns {string} - 'high', 'low', or 'normal'
 */
export const getMeasurementAlertStatus = (value, min, max) => {
  const numericValue = Number(value);
  const numericMin = Number(min);
  const numericMax = Number(max);

  if (
    value === null ||
    value === undefined ||
    value === '' ||
    value === 'N/A' ||
    min === null ||
    min === undefined ||
    min === '' ||
    max === null ||
    max === undefined ||
    max === '' ||
    Number.isNaN(numericValue) ||
    numericValue <= 0 ||
    Number.isNaN(numericMin) ||
    Number.isNaN(numericMax)
  ) {
    return 'normal';
  }
  
  // Inclusive target boundaries:
  // value === min or value === max is considered normal.
  if (numericValue > numericMax) {
    return 'high';
  } else if (numericValue < numericMin) {
    return 'low';
  }
  return 'normal';
};

/**
 * Get CSS class for measurement value based on alert status
 * @param {string} status - 'high', 'low', or 'normal'
 * @returns {string} - CSS class name
 */
export const getMeasurementValueClass = (status) => {
  switch (status) {
    case 'high':
      return 'value-high';
    case 'low':
      return 'value-low';
    default:
      return 'value-normal';
  }
};

/** CSS suffix for vital alert level (high = red, low = yellow, normal = green chip). */
export const getVitalLevelClassSuffix = (status) => {
  if (status === 'high') return ' is-high';
  if (status === 'low') return ' is-low';
  if (status === 'normal') return ' is-ok';
  return '';
};

/** CSS suffix for vital value text (high = red, low = yellow, normal = green). */
export const getVitalValueClassSuffix = (status) => {
  if (status === 'high') return ' is-high';
  if (status === 'low') return ' is-low';
  if (status === 'normal') return ' is-ok';
  return '';
};

/** Standard BMI category thresholds (WHO adult). */
export const BMI_CATEGORY = {
  UNDERWEIGHT: 'underweight',
  NORMAL: 'normal',
  OVERWEIGHT: 'overweight',
  OBESE: 'obese'
};

/**
 * Classify BMI into underweight / normal / overweight / obese.
 * Underweight < 18.5 | Normal 18.5–24.9 | Overweight 25.0–29.9 | Obese ≥ 30.0
 */
export const getBmiCategoryStatus = (bmiValue, thresholds = {}) => {
  const value = Number(bmiValue);
  if (!Number.isFinite(value)) return BMI_CATEGORY.NORMAL;

  const normalMin = Number(thresholds.bmiNormal ?? 18.5);
  const overweightMin = Number(thresholds.bmiOverweight ?? 25);
  const obeseMin = Number(thresholds.bmiObese ?? 30);

  if (value < normalMin) return BMI_CATEGORY.UNDERWEIGHT;
  if (value < overweightMin) return BMI_CATEGORY.NORMAL;
  if (value < obeseMin) return BMI_CATEGORY.OVERWEIGHT;
  return BMI_CATEGORY.OBESE;
};

/** CSS suffix for BMI: underweight=yellow, normal=green, overweight=orange, obese=red. */
export const getBmiClassSuffix = (status) => {
  if (status === BMI_CATEGORY.UNDERWEIGHT || status === 'low') return ' is-underweight';
  if (status === BMI_CATEGORY.NORMAL) return ' is-ok';
  if (status === BMI_CATEGORY.OVERWEIGHT) return ' is-overweight';
  if (status === BMI_CATEGORY.OBESE || status === 'high') return ' is-obese';
  return '';
};

/** Table / alert class for BMI category (Patient Management style). */
export const getBmiAlertClass = (status) => {
  if (status === BMI_CATEGORY.UNDERWEIGHT || status === 'low') return 'alert-underweight';
  if (status === BMI_CATEGORY.NORMAL) return 'alert-normal';
  if (status === BMI_CATEGORY.OVERWEIGHT) return 'alert-overweight';
  if (status === BMI_CATEGORY.OBESE || status === 'high') return 'alert-obese';
  return '';
};

/** Measurement-table value class for BMI category. */
export const getBmiTableValueClass = (status) => {
  if (status === BMI_CATEGORY.UNDERWEIGHT || status === 'low') return 'value-underweight';
  if (status === BMI_CATEGORY.NORMAL) return 'value-normal';
  if (status === BMI_CATEGORY.OVERWEIGHT) return 'value-overweight';
  if (status === BMI_CATEGORY.OBESE || status === 'high') return 'value-obese';
  return 'value-normal';
};

/** Worst BP level when sys/dia may differ (high takes precedence over low). */
export const getCombinedBpAlertLevel = (bpStatus) => {
  if (!bpStatus?.isOutOfRange) return 'normal';
  if (bpStatus.sysStatus === 'high' || bpStatus.diaStatus === 'high') return 'high';
  return 'low';
};

/**
 * Check if BP values are out of target range
 * @param {Object} bpData - Blood pressure data with systolic_pressure and diastolic_pressure
 * @param {Object} target - Target range with systolicMin, systolicMax, diastolicMin, diastolicMax
 * @returns {Object} - { sysStatus: 'high'|'low'|'normal', diaStatus: 'high'|'low'|'normal', isOutOfRange: boolean }
 */
export const checkBPValues = (bpData, target) => {
  if (!bpData || !target) {
    return { sysStatus: 'normal', diaStatus: 'normal', isOutOfRange: false };
  }

  const pickFirstNumber = (...values) => {
    for (const value of values) {
      if (value === null || value === undefined || value === '' || value === 'N/A') continue;
      const parsed = Number(value);
      if (!Number.isNaN(parsed) && parsed > 0) return parsed;
    }
    return null;
  };

  const sys = bpData.systolic_pressure ?? bpData.systolic ?? bpData.last_systolic ?? bpData.sys;
  const dia = bpData.diastolic_pressure ?? bpData.diastolic ?? bpData.last_diastolic ?? bpData.dia;
  const sysMin = pickFirstNumber(
    target.systolicMin,
    target.sysMin,
    target.systolic_min,
    target.systolic_pressure_min,
    target.target_sys_min,
    target.sys_min
  );
  const sysMax = pickFirstNumber(
    target.systolicMax,
    target.sysMax,
    target.systolic_max,
    target.systolic_pressure_max,
    target.target_sys_max,
    target.sys_max
  );
  const diaMin = pickFirstNumber(
    target.diastolicMin,
    target.diaMin,
    target.diastolic_min,
    target.diastolic_pressure_min,
    target.target_dia_min,
    target.dia_min
  );
  const diaMax = pickFirstNumber(
    target.diastolicMax,
    target.diaMax,
    target.diastolic_max,
    target.diastolic_pressure_max,
    target.target_dia_max,
    target.dia_max
  );
  
  const sysStatus = (sys != null && sys !== 'N/A' && Number(sys) > 0) ? getMeasurementAlertStatus(sys, sysMin, sysMax) : 'normal';
  const diaStatus = (dia != null && dia !== 'N/A' && Number(dia) > 0) ? getMeasurementAlertStatus(dia, diaMin, diaMax) : 'normal';
  const isOutOfRange = sysStatus !== 'normal' || diaStatus !== 'normal';
  
  return { sysStatus, diaStatus, isOutOfRange };
};

/**
 * Check if BG value is out of target range
 * @param {number|Object} bgValue - Blood glucose value or measurement object
 * @param {Object} target - Target range with min and max
 * @returns {string} - 'high', 'low', or 'normal'
 */
export const checkBGValue = (bgValue, target) => {
  let val = bgValue;
  if (bgValue && typeof bgValue === 'object') {
    val =
      bgValue.blood_glucose_value_1 ??
      bgValue.blood_glucose_value_2 ??
      bgValue.blood_glucose_value_3 ??
      bgValue.blood_glucose_value ??
      bgValue.glucose_value ??
      bgValue.glucose ??
      bgValue.last_glucose;
  }
  const numeric = Number(val);
  if (
    val === null ||
    val === undefined ||
    val === '' ||
    val === 'N/A' ||
    Number.isNaN(numeric) ||
    numeric <= 0 ||
    !target
  ) {
    return 'normal';
  }

  const pickFirstNumber = (...values) => {
    for (const value of values) {
      if (value === null || value === undefined || value === '') continue;
      const parsed = Number(value);
      if (!Number.isNaN(parsed)) return parsed;
    }
    return null;
  };

  const min = pickFirstNumber(
    target.minimum,
    target.min,
    target.bgMin,
    target.target_bg_min,
    target.bg_min,
    target.minBg
  );
  const max = pickFirstNumber(
    target.maximum,
    target.max,
    target.bgMax,
    target.target_bg_max,
    target.bg_max,
    target.maxBg
  );
  if (min == null || max == null) return 'normal';

  return getMeasurementAlertStatus(numeric, min, max);
};

/**
 * Check pulse against target range (defaults 60–100 only when target bounds are missing).
 * @returns {string|null} - 'high', 'low', 'normal', or null when value missing
 */
export const checkPulseValue = (pulseValue, target) => {
  if (pulseValue === null || pulseValue === undefined || pulseValue === '' || pulseValue === 'N/A') return null;
  const numeric = Number(pulseValue);
  if (Number.isNaN(numeric) || numeric <= 0) return null;

  const pickFirstNumber = (...values) => {
    for (const value of values) {
      if (value === null || value === undefined || value === '') continue;
      const parsed = Number(value);
      if (!Number.isNaN(parsed)) return parsed;
    }
    return null;
  };

  const min = pickFirstNumber(target?.pulseMin, target?.pulse_min, target?.minPulse, target?.min) ?? 60;
  const max = pickFirstNumber(target?.pulseMax, target?.pulse_max, target?.maxPulse, target?.max) ?? 100;
  return getMeasurementAlertStatus(numeric, min, max);
};

/**
 * ForaCare portal / device ecosystem uses 2.2 (not scientific 2.20462).
 * Keep one factor app-wide so EVitals lbs match ForaCare.
 */
export const KG_TO_LB = 2.2;

export const kgToLb = (kg) => {
  const n = Number(kg);
  return Number.isFinite(n) ? n * KG_TO_LB : null;
};

export const lbToKg = (lb) => {
  const n = Number(lb);
  return Number.isFinite(n) ? n / KG_TO_LB : null;
};

/**
 * Check if Weight value is out of target range
 * @param {number|Object} weightKg - Weight in kilograms or weight measurement object
 * @param {Object} target - Target range with weightMin and weightMax (in pounds)
 * @returns {string} - 'high', 'low', or 'normal'
 */
export const checkWeightValue = (weightKgOrLbs, target) => {
  let weightInLb = null;

  if (weightKgOrLbs && typeof weightKgOrLbs === 'object') {
    const raw = weightKgOrLbs.weight_lbs ?? weightKgOrLbs.weight ?? weightKgOrLbs.last_weight;
    const num = Number(raw);
    if (!Number.isNaN(num) && num > 0) {
      weightInLb = num;
    }
  } else {
    const numeric = Number(weightKgOrLbs);
    if (!Number.isNaN(numeric) && numeric > 0) {
      weightInLb = numeric;
    }
  }

  if (
    weightInLb === null ||
    weightInLb === undefined ||
    Number.isNaN(weightInLb) ||
    weightInLb <= 0 ||
    !target
  ) {
    return 'normal';
  }

  const pickFirstNumber = (...values) => {
    for (const value of values) {
      if (value === null || value === undefined || value === '') continue;
      const parsed = Number(value);
      if (!Number.isNaN(parsed)) return parsed;
    }
    return null;
  };

  const min = pickFirstNumber(
    target.weightMin,
    target.weight_min,
    target.target_weight_min,
    target.minLbs,
    target.min,
    target.minimum
  ) ?? 66;

  const max = pickFirstNumber(
    target.weightMax,
    target.weight_max,
    target.target_weight_max,
    target.maxLbs,
    target.max,
    target.maximum
  ) ?? 220;
  
  return getMeasurementAlertStatus(weightInLb, min, max);
};

/**
 * Check if Body Fat value is out of target range
 * @param {number|Object} fatValue - Fat percentage or measurement object
 * @param {Object} target - Target range with bodyFatMin and bodyFatMax
 * @returns {string} - 'high', 'low', or 'normal'
 */
export const checkFatValue = (fatValue, target) => {
  let val = fatValue;
  if (fatValue && typeof fatValue === 'object') {
    val = fatValue.fat ?? fatValue.body_fat;
  }
  const numeric = Number(val);
  if (
    val === null ||
    val === undefined ||
    val === '' ||
    val === 'N/A' ||
    Number.isNaN(numeric) ||
    numeric <= 0
  ) {
    return 'normal';
  }

  const min = target?.bodyFatMin ?? target?.body_fat_min ?? 14;
  const max = target?.bodyFatMax ?? target?.body_fat_max ?? 27;

  if (numeric > max) return 'high';
  if (numeric < min) return 'low';
  return 'normal';
};

/**
 * Get display color for vital / BMI / Fat alert status
 */
export const getVitalStatusColor = (status) => {
  if (status === 'high' || status === 'obese' || status === BMI_CATEGORY.OBESE) return '#d32f2f'; // Red (Obese / High)
  if (status === 'overweight' || status === BMI_CATEGORY.OVERWEIGHT) return '#ea580c'; // Vibrant Deep Orange (Overweight)
  if (status === 'underweight' || status === BMI_CATEGORY.UNDERWEIGHT) return '#d97706'; // Crisp Golden Yellow / Amber (Underweight)
  if (status === 'low') return '#f57c00'; // Low
  return '#15803d'; // Green (Normal)
};

/**
 * Extract YYYY-MM-DD from DB/API values without browser timezone shifting.
 * Handles: "2026-07-27", "2026-07-27 16:27:00", "2026-07-27T16:27:00.000Z"
 */
export const extractWallClockDateYmd = (value) => {
  if (value == null || value === '') return '';
  const match = String(value).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[1]}-${match[2]}-${match[3]}` : '';
};

/**
 * Extract HH:mm:ss clock digits without timezone conversion.
 * Handles TIME strings, "YYYY-MM-DD HH:mm:ss", and ISO "...T16:27:00.000Z".
 */
export const extractWallClockTimeHms = (value) => {
  if (value == null || value === '') return '';
  const raw = String(value).trim();
  if (/AM|PM/i.test(raw)) return raw;

  const isoMatch = raw.match(/T(\d{2}):(\d{2})(?::(\d{2}))?/);
  if (isoMatch) {
    return `${isoMatch[1]}:${isoMatch[2]}:${isoMatch[3] || '00'}`;
  }

  const spaceTime = raw.includes(' ')
    ? raw.split(/\s+/).slice(1).join(' ')
    : raw;
  const timeMatch = spaceTime.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!timeMatch) return '';
  return `${String(timeMatch[1]).padStart(2, '0')}:${timeMatch[2]}:${timeMatch[3] || '00'}`;
};

/** Local Date at midnight for calendar math (week buckets) — no UTC parse. */
export const parseWallClockDateLocal = (value) => {
  const ymd = extractWallClockDateYmd(value);
  if (!ymd) return null;
  const [year, month, day] = ymd.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

export const getMeasurementWallClockDateYmd = (measurement) =>
  extractWallClockDateYmd(measurement?.measurement_date) ||
  extractWallClockDateYmd(measurement?.measure_date_time) ||
  extractWallClockDateYmd(measurement?.measure_new_date_time) ||
  extractWallClockDateYmd(measurement?.created_at) ||
  '';

export const getMeasurementWallClockTimeHms = (measurement) =>
  extractWallClockTimeHms(measurement?.measurement_time) ||
  extractWallClockTimeHms(measurement?.measure_date_time) ||
  extractWallClockTimeHms(measurement?.measure_new_date_time) ||
  '';

const getMeasurementDatePart = (measurement) => getMeasurementWallClockDateYmd(measurement);

const getMeasurementTimePart = (measurement) => {
  const hms = getMeasurementWallClockTimeHms(measurement);
  if (hms) return hms.slice(0, 5);
  return extractWallClockTimeHms(measurement?.created_at).slice(0, 5) || '';
};

const getMeasurementLatestStamp = (measurement) =>
  new Date(
    measurement.updated_at ||
    measurement.created_at ||
    measurement.measure_new_date_time ||
    measurement.measurement_date ||
    0
  ).getTime();

const getMeasurementValueSignature = (measurement) => {
  const pick = (value) => (value === undefined || value === null ? '' : String(value).trim());
  return [
    pick(measurement.systolic_pressure),
    pick(measurement.diastolic_pressure),
    pick(measurement.pulse),
    // BG may come in either normalized or raw DB shape.
    pick(measurement.blood_glucose_value),
    pick(measurement.blood_glucose_value_1),
    pick(measurement.blood_glucose_value_2),
    pick(measurement.blood_glucose_value_3),
    // Weight may come in either normalized or raw DB shape.
    pick(measurement.weight),
    pick(measurement.body_fat),
    pick(measurement.fat),
    pick(measurement.bmi)
  ].join('|');
};

/**
 * Keep latest only for exact duplicates:
 * - same date+time
 * - same reading values
 * If date+time are same but values differ, keep both.
 */
export const dedupeMeasurementsByDateTimeKeepLatest = (items = []) => {
  const sorted = [...(Array.isArray(items) ? items : [])].sort((a, b) => {
    const stampDiff = getMeasurementLatestStamp(b) - getMeasurementLatestStamp(a);
    if (stampDiff !== 0) return stampDiff;
    return (Number(b?.id) || 0) - (Number(a?.id) || 0);
  });

  const seen = new Set();
  const deduped = [];

  sorted.forEach((measurement) => {
    const key = `${getMeasurementDatePart(measurement)}|${getMeasurementTimePart(measurement)}|${getMeasurementValueSignature(measurement)}`;
    if (seen.has(key)) return;
    seen.add(key);
    deduped.push(measurement);
  });

  return deduped;
};

export const formatMeasurementTableDate = (dateString) => {
  const ymd = extractWallClockDateYmd(dateString);
  if (!ymd) return '';
  const [year, month, day] = ymd.split('-');
  return `${month}-${day}-${year}`;
};

export const formatMeasurementTableTime = (timeString) => {
  if (timeString == null || timeString === '') return '';
  const raw = String(timeString).trim();
  if (/AM|PM/i.test(raw)) return raw;

  const hms = extractWallClockTimeHms(raw);
  if (!hms) return '';
  const [hStr, mStr] = hms.split(':');
  const h = Number(hStr);
  if (!Number.isFinite(h)) return '';
  const period = h >= 12 ? 'PM' : 'AM';
  const displayHours = h % 12 || 12;
  return `${displayHours}:${mStr} ${period}`;
};

export const formatMeasurementTableDateTime = (measurement) => {
  if (!measurement) return '';
  const date = formatMeasurementTableDate(getMeasurementWallClockDateYmd(measurement));
  const time = formatMeasurementTableTime(getMeasurementWallClockTimeHms(measurement));
  if (date && time) return `${date}, ${time}`;
  return date || time || '';
};


